import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Users, ArrowRight, Phone, Loader2, Home, ChevronRight, Heart, Shield, Sparkles } from 'lucide-react';
import { API } from '../utils/api';
import NotFoundPage from './NotFoundPage';

var AUDIENCE_META = {
  adolescentes: {
    name: 'Adolescentes',
    title: 'Centros de Rehabilitación para Adolescentes en Chile',
    tagline: 'Programas especializados para menores de 18 años',
    description: 'Encuentra centros de rehabilitación diseñados específicamente para adolescentes y jóvenes en Chile. Programas con enfoque familiar, educativo y terapéutico, con atención profesional en adicciones a marihuana, cocaína, alcohol y adicciones conductuales (redes sociales, videojuegos).',
    shortDesc: 'Centros con programas especializados para adolescentes con enfoque familiar y educativo.',
    icon: Sparkles,
    specializations: ['adolescentes', 'jovenes', 'menores'],
    bullets: [
      'Equipo multidisciplinario: psiquiatra infanto-juvenil, psicólogo, terapeuta familiar.',
      'Programas que no interrumpen la escolaridad (apoyo académico integrado).',
      'Trabajo con la familia como parte central del tratamiento.',
      'Intervención temprana en consumo de marihuana, alcohol y adicciones digitales.',
      'Detección y tratamiento de patología dual (ansiedad, depresión, TDAH).'
    ]
  },
  mujeres: {
    name: 'Mujeres',
    title: 'Centros de Rehabilitación para Mujeres en Chile',
    tagline: 'Programas exclusivos con enfoque de género',
    description: 'Centros de rehabilitación en Chile con programas exclusivos para mujeres. Enfoque de género que aborda la dependencia emocional, historia de violencia, maternidad y contextos socio-culturales que diferencian la adicción femenina.',
    shortDesc: 'Espacios seguros con equipos sensibilizados y programas exclusivos para mujeres.',
    icon: Heart,
    specializations: ['mujeres', 'genero-femenino'],
    bullets: [
      'Espacios exclusivos o con divisiones para mujeres.',
      'Abordaje de trauma y violencia de género.',
      'Apoyo a madres con hijos menores.',
      'Programas para adicción a benzodiacepinas y alcohol (alta prevalencia femenina).',
      'Terapia grupal con pares en contextos seguros.'
    ]
  },
  'adultos-mayores': {
    name: 'Adultos Mayores',
    title: 'Centros de Rehabilitación para Adultos Mayores en Chile',
    tagline: 'Atención especializada para personas de la tercera edad',
    description: 'Centros de rehabilitación en Chile con programas adaptados a adultos mayores de 60 años. Tratamiento de adicciones a benzodiacepinas, alcohol y medicamentos de prescripción con enfoque geriátrico integral.',
    shortDesc: 'Programas con enfoque geriátrico para adicciones en adultos mayores.',
    icon: Shield,
    specializations: ['adultos-mayores', 'tercera-edad'],
    bullets: [
      'Equipo con experiencia en geriatría y gerontopsiquiatría.',
      'Manejo especializado de dependencia a benzodiacepinas y analgésicos.',
      'Consideración de comorbilidades médicas (diabetes, hipertensión, demencia incipiente).',
      'Desintoxicación supervisada con monitoreo médico continuo.',
      'Apoyo familiar e integración con cuidadores.'
    ]
  }
};

export default function AudiencePage() {
  var params = useParams();
  var slug = params.audience;
  var meta = AUDIENCE_META[slug];
  var [centers, setCenters] = useState([]);
  var [loading, setLoading] = useState(true);

  useEffect(function() {
    if (!meta) { setLoading(false); return; }
    setLoading(true);
    Promise.all(meta.specializations.map(function(spec) {
      return axios.get(API + '/centers/public?specialization=' + encodeURIComponent(spec) + '&limit=24').catch(function() { return { data: { centers: [] } }; });
    })).then(function(results) {
      var all = [];
      var seen = {};
      results.forEach(function(r) {
        (r.data.centers || []).forEach(function(c) {
          if (!seen[c.center_id]) { seen[c.center_id] = true; all.push(c); }
        });
      });
      setCenters(all);
    }).finally(function() { setLoading(false); });
  }, [slug]);

  if (!meta) {
    // Público no existe en nuestro catálogo → 404 con noindex (Soft 404 fix para GSC)
    return <NotFoundPage />;
  }

  var Icon = meta.icon;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      
        <title>{meta.title + ' | SinAdicciones.org'}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={'https://sinadicciones.org/publico/' + slug} />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={'https://sinadicciones.org/publico/' + slug} />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": meta.title,
          "description": meta.description,
          "url": 'https://sinadicciones.org/publico/' + slug
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
            { "@type": "ListItem", "position": 2, "name": meta.name }
          ]
        })}</script>
      
      <Header />
      <main className="pt-20 pb-16" data-testid="audience-page">
        {/* Breadcrumbs */}
        <div className="bg-slate-900 border-b border-slate-800">
          <div className="max-w-6xl mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-slate-400">
              <Link to="/" className="hover:text-emerald-400 flex items-center gap-1"><Home className="w-3 h-3" />Inicio</Link>
              <ChevronRight className="w-3 h-3" />
              <span className="text-slate-300">{meta.name}</span>
            </nav>
          </div>
        </div>

        <section className="bg-gradient-to-b from-emerald-950/30 to-slate-950 border-b border-slate-800">
          <div className="max-w-5xl mx-auto px-4 py-12">
            <div className="flex items-center gap-3 text-emerald-400 text-sm font-medium mb-3">
              <Icon className="w-5 h-5" /><span>{meta.tagline}</span>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4" data-testid="audience-title">{meta.title}</h1>
            <p className="text-lg text-slate-400 max-w-3xl leading-relaxed">{meta.description}</p>
          </div>
        </section>

        <section className="max-w-5xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold mb-6">¿Qué buscar en un centro para {meta.name.toLowerCase()}?</h2>
          <ul className="space-y-3">
            {meta.bullets.map(function(b, i) {
              return (
                <li key={i} className="flex items-start gap-3 bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                  <div className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-bold flex-shrink-0">{i + 1}</div>
                  <span className="text-slate-300">{b}</span>
                </li>
              );
            })}
          </ul>
        </section>

        <section className="max-w-6xl mx-auto px-4 py-8">
          <h2 className="text-2xl font-bold mb-6">Centros recomendados para {meta.name.toLowerCase()}</h2>
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
          ) : centers.length === 0 ? (
            <Card className="bg-slate-900/50 border-slate-800">
              <CardContent className="p-8 text-center">
                <p className="text-slate-400 mb-4">Estamos ampliando nuestro directorio con centros especializados para {meta.name.toLowerCase()}.</p>
                <Button asChild variant="outline" className="border-emerald-600 text-emerald-400 hover:bg-emerald-600/10">
                  <Link to="/centros-de-rehabilitacion-drogas-chile" data-testid="audience-cta-all">Ver todos los centros</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="audience-centers-grid">
              {centers.slice(0, 18).map(function(c) {
                return (
                  <Link key={c.center_id} to={'/centros/' + (c.slug || c.center_id)} className="group" data-testid={'audience-center-' + c.center_id}>
                    <Card className="bg-slate-900/50 border-slate-800 hover:border-emerald-500/50 transition-all h-full">
                      <CardContent className="p-5">
                        <h3 className="font-semibold text-slate-50 group-hover:text-emerald-400 transition-colors line-clamp-2 mb-2">{c.name}</h3>
                        <p className="text-sm text-slate-400 mb-3">{c.city}</p>
                        <span className="text-emerald-400 inline-flex items-center gap-1 text-sm">Ver centro <ArrowRight className="w-3 h-3" /></span>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          )}
        </section>

        <section className="max-w-5xl mx-auto px-4 py-12">
          <div className="bg-gradient-to-br from-emerald-900/30 to-slate-900 border border-emerald-500/20 rounded-2xl p-8 text-center">
            <Phone className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Necesitas orientación urgente</h2>
            <p className="text-slate-400 mb-6">Fonodrogas SENDA · Atención 24/7 gratuita y confidencial.</p>
            <Button asChild size="lg" className="bg-emerald-600 hover:bg-emerald-700">
              <a href="tel:1412" data-testid="audience-fonodrogas">Llamar 1412</a>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
